import _ from 'underscore';
import CS from 'stores/anyguide/collection';
import type Backbone from 'backbone';

interface RequireOptions {
  forceFetch?: boolean;
  sync?: (state: any) => void;
  onResolved?: () => void;
}

interface ModelConstructor {
  new (attributes?: any, options?: any): Backbone.Model;
  prototype: Backbone.Model;
}

interface ModelStoreInterface {
  CACHE_EXPIRE: number;
  urlPrefix: string;
  register(name: string, constructor: ModelConstructor): void;
  buildRootUrl(modelConstructor: ModelConstructor): string;
  bind(model: Backbone.Model, fn: (attrs: any) => void): void;
  unbind(model: Backbone.Model, fn: (attrs: any) => void): void;
  bindOnce(modelType: string, id: number, fn: (attrs: any) => void): void;
  require(name: string, id?: number, opts?: RequireOptions): Backbone.Model;
  remove(name: string, id: number, opts?: RequireOptions): Promise<void>;
  removeModel(model: Backbone.Model, opts?: RequireOptions): Promise<void>;
}

const registeredModels: Record<string, ModelConstructor> = {};
const instanceCache: Record<string, Record<number, Backbone.Model>> = {};
const callbacks: Record<string, ((attrs: any) => void)[]> = {};

class ModelStore implements ModelStoreInterface {
  CACHE_EXPIRE = 50000;

  urlPrefix = '/api';

  _registeredModels: any;

  register(name: string, constructor: ModelConstructor): void {
    // @ts-ignore
    constructor.prototype.name = name;
    constructor.prototype.urlRoot = this.buildRootUrl(constructor);
    registeredModels[name] = constructor;
    instanceCache[name] = {};
  }

  buildRootUrl(modelConstructor: ModelConstructor): string {
    // @ts-ignore
    let root = `${this.urlPrefix}/${modelConstructor.prototype.urlSegment}`;
    // @ts-ignore
    if (modelConstructor.prototype.format) {
      // @ts-ignore
      root += `.${modelConstructor.prototype.format}`;
    }
    return root;
  }

  bind(model: Backbone.Model, fn: (attrs: any) => void): void {
    callbacks[model.cid] = callbacks[model.cid] || [];
    callbacks[model.cid].push(fn);
    // @ts-ignore
    if (model.isNew() || model._fetchedOnce) {
      fn(model.attributes);
    }
  }

  unbind(model: Backbone.Model, fn: (attrs: any) => void): void {
    callbacks[model.cid] = callbacks[model.cid] || [];
    const index = _.indexOf(callbacks[model.cid], fn);
    if (index !== -1) {
      callbacks[model.cid].splice(index, 1);
    }
  }

  bindOnce(modelType: string, id: number, fn: (attrs: any) => void): void {
    const model = this.require(modelType, id);
    if (!model) {
      console.error(`Model not found for modelType: ${modelType}, id: ${id}`);
      return;
    }
    const callback = (attrs: any) => {
      fn(attrs);
      this.unbind(model, callback);
    };
    this.bind(model, callback);
  }

  require(
    name: string,
    id?: number,
    opts: RequireOptions = {}
  ): Backbone.Model {
    if (id === undefined) {
      return this._requireNew(name);
    }
    if (this._modelCached(name, id)) {
      return this._requireFromCache(name, id, opts);
    }
    return this._requireFromServer(name, id);
  }

  private _requireNew(name: string): Backbone.Model {
    const constructor = this._getModel(name);
    if (!constructor) {
      throw new TypeError(`Name passed (${name}) wasn't registered as a model`);
    }
    const model = new constructor();
    const firstSync = (attrs: any) => {
      if (attrs.id !== undefined) {
        // @ts-ignore
        model._fetchedOnce = true;
        this._cacheModel(name, model);
        CS._refreshCollections(name);
        this.unbind(model, firstSync);
      }
    };
    this.bind(model, firstSync);
    this._registerCallbacks(model);
    return model;
  }

  private _requireFromCache(
    name: string,
    id: number,
    opts: RequireOptions
  ): Backbone.Model {
    const model = instanceCache[name][id];
    if (opts.forceFetch || this._shouldFetch(model)) {
      this._fetchModel(model);
    }
    return model;
  }

  private _requireFromServer(name: string, id: number): Backbone.Model {
    const constructor = this._getModel(name);
    if (!constructor) {
      throw new TypeError(`Name passed (${name}) wasn't registered as a model`);
    }
    const model = new constructor({ id });
    this._cacheModel(name, model);
    this._registerCallbacks(model);
    const data: any = name === 'tour' ? { fields: ['name'] } : {};
    this._fetchModel(model, data);
    return model;
  }

  private _registerCallbacks(model: Backbone.Model): void {
    model.on('sync', (syncedModel: Backbone.Model) => {
      const state = syncedModel.attributes;
      _.each(_.clone(callbacks[syncedModel.cid]), (fn: (arg0: any) => void) => {
        fn(state);
      });
    });
    model.on(
      'error',
      (errorModel: Backbone.Model, resp: { responseJSON: any }) => {
        const state = _.extend(
          {},
          errorModel.attributes,
          resp.responseJSON || {}
        );
        _.each(
          _.clone(callbacks[errorModel.cid]),
          (fn: (arg0: any) => void) => {
            fn(state);
          }
        );
      }
    );
  }

  private _shouldFetch(model: Backbone.Model): boolean {
    // @ts-ignore
    return model._lastFetchAt + this.CACHE_EXPIRE < new Date().getTime();
  }

  private _modelCached(name: string, id: number): boolean {
    return instanceCache[name] && instanceCache[name][id] !== undefined;
  }

  private _fetchModel(model: Backbone.Model, data?: any): void {
    model.fetch({
      data,
      success: () => {
        // @ts-ignore
        model._fetching = false;
        // @ts-ignore
        model._fetchedOnce = true;
      },
      error: () => {
        // @ts-ignore
        model._fetching = false;
      },
    });
    // @ts-ignore
    model._fetching = true;
    // @ts-ignore
    model._lastFetchAt = new Date().getTime();
  }

  private _cacheModel(name: string, model: Backbone.Model): void {
    // @ts-ignore
    instanceCache[name][model.id] = model;
  }

  remove(name: string, id: number, opts: RequireOptions = {}): Promise<void> {
    const model = this.require(name, id, opts);
    return this.removeModel(model, opts);
  }

  removeModel(model: Backbone.Model, opts: any): Promise<void> {
    const result = model.destroy(opts);
    const promise = result
      ? Promise.resolve(result)
      : Promise.reject(new Error('Destroy failed'));
    promise.then(() => {
      if (opts.onResolved) {
        opts.onResolved();
      }
      model.trigger('MS:remove', model);
    });
    return promise;
  }

  private _getModel(name: string): ModelConstructor | undefined {
    return registeredModels[name];
  }
}

export default new ModelStore();
